import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getText, analyzeText, listProfiles, synthesizeSpeech, getTTSStatus, createReadingSession } from "../lib/api";
import CelebrationOverlay from "../components/CelebrationOverlay";
import ReadingTimer from "../components/ReadingTimer";
import {
  ArrowLeft, Play, Pause, SkipForward, SkipBack,
  Volume2, VolumeX, ZoomIn, ZoomOut, Settings, X
} from "lucide-react";

export default function ReaderPage() {
  const { textId } = useParams();
  const navigate = useNavigate();

  const [doc, setDoc] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const [fontSize, setFontSize] = useState(2); // 0=S, 1=M, 2=L, 3=XL
  const [ttsAvailable, setTtsAvailable] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentSentenceIdx, setCurrentSentenceIdx] = useState(-1);
  const [currentWordIdx, setCurrentWordIdx] = useState(-1);
  const [guidedMode, setGuidedMode] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [readingTime, setReadingTime] = useState(0);
  const audioRef = useRef(null);
  const sentenceRefs = useRef([]);

  const fontSizes = ["text-lg leading-[2]", "text-xl leading-[2.2]", "text-2xl leading-[2.4]", "text-3xl leading-[2.6]"];
  const fontLabels = ["S", "M", "L", "XL"];

  // Load data
  useEffect(() => {
    if (!textId) return;
    Promise.all([
      getText(textId),
      listProfiles(),
      getTTSStatus().catch(() => ({ data: { available: false } })),
    ]).then(([textRes, profilesRes, ttsRes]) => {
      setDoc(textRes.data);
      setProfiles(profilesRes.data);
      setTtsAvailable(ttsRes.data.available);
      const pid = textRes.data.profile_id || profilesRes.data[0]?.id;
      setSelectedProfileId(pid);
    }).catch(console.error);
  }, [textId]);

  // Analyze when profile changes
  useEffect(() => {
    if (!doc || !selectedProfileId) return;
    analyzeText(doc.raw_text, selectedProfileId)
      .then((res) => {
        setAnalysis(res.data.analysis);
        setProfile(res.data.profile);
      })
      .catch(console.error);
  }, [doc, selectedProfileId]);

  // Play sentence audio
  const playSentence = useCallback(async (idx) => {
    if (!ttsAvailable || !analysis?.sentences?.[idx]) return;
    const sentence = analysis.sentences[idx];

    try {
      setCurrentSentenceIdx(idx);
      setCurrentWordIdx(-1);
      setIsPlaying(true);

      // Scroll to sentence
      if (sentenceRefs.current[idx]) {
        sentenceRefs.current[idx].scrollIntoView({ behavior: "smooth", block: "center" });
      }

      const blob = await synthesizeSpeech(sentence.text, 0.85);
      const url = URL.createObjectURL(blob.data);

      if (audioRef.current) {
        audioRef.current.pause();
        URL.revokeObjectURL(audioRef.current.src);
      }

      const audio = new Audio(url);
      audioRef.current = audio;

      // Guided mode: estimate word timing and highlight words progressively
      if (guidedMode) {
        const wordTokens = sentence.words.filter((w) => w.type === "word");
        const totalChars = wordTokens.reduce((sum, w) => sum + w.text.length, 0);
        audio.ontimeupdate = () => {
          if (!audio.duration) return;
          const progress = audio.currentTime / audio.duration;
          let charsSoFar = 0;
          for (let wi = 0; wi < wordTokens.length; wi++) {
            charsSoFar += wordTokens[wi].text.length;
            if (charsSoFar / totalChars >= progress) {
              setCurrentWordIdx(sentence.words.indexOf(wordTokens[wi]));
              break;
            }
          }
        };
      }

      audio.onended = () => {
        setIsPlaying(false);
        setCurrentWordIdx(-1);
        URL.revokeObjectURL(url);
        if (idx + 1 < analysis.sentences.length) {
          playSentence(idx + 1);
        } else {
          setCurrentSentenceIdx(-1);
          // Reading complete! Show celebration
          setShowCelebration(true);
        }
      };

      audio.onerror = () => {
        setIsPlaying(false);
        setCurrentWordIdx(-1);
        URL.revokeObjectURL(url);
      };

      await audio.play();
    } catch (err) {
      console.error("TTS playback failed:", err);
      setIsPlaying(false);
    }
  }, [ttsAvailable, analysis, guidedMode]);

  const handlePlayPause = () => {
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      const idx = currentSentenceIdx >= 0 ? currentSentenceIdx : 0;
      playSentence(idx);
    }
  };

  const handleNext = () => {
    if (!analysis?.sentences) return;
    const next = Math.min((currentSentenceIdx >= 0 ? currentSentenceIdx : -1) + 1, analysis.sentences.length - 1);
    if (audioRef.current) audioRef.current.pause();
    playSentence(next);
  };

  const handlePrev = () => {
    if (!analysis?.sentences) return;
    const prev = Math.max((currentSentenceIdx >= 0 ? currentSentenceIdx : 1) - 1, 0);
    if (audioRef.current) audioRef.current.pause();
    playSentence(prev);
  };

  const handleStop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsPlaying(false);
    setCurrentSentenceIdx(-1);
  };

  const rules = profile?.rules || {};
  const syllableColors = rules.syllable_coloring?.enabled
    ? (rules.syllable_coloring.colors || ["#1D4ED8", "#C2410C"])
    : null;
  const showSilent = rules.silent_letters?.enabled || false;
  const lineAlt = rules.line_alternation?.enabled
    ? (rules.line_alternation.colors || ["#EFF6FF", "#FFF7ED"])
    : null;
  const highlightGraphemes = rules.grapheme_highlight?.enabled
    ? new Set(rules.grapheme_highlight.graphemes || [])
    : null;

  if (!doc || !analysis) {
    return (
      <div className="min-h-screen bg-orange-50 flex items-center justify-center">
        <img src="/LEcoleDeNavi4.png" alt="Chargement" className="h-20 mascot-float" />
      </div>
    );
  }

  return (
    <div data-testid="reader-page" className="min-h-screen bg-orange-50 flex flex-col">
      {/* Top bar */}
      <div className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b-2 border-orange-100 shadow-sm">
        <div className="flex items-center justify-between h-14 px-4 max-w-4xl mx-auto">
          <button
            data-testid="reader-back-btn"
            onClick={() => navigate(-1)}
            className="p-2.5 rounded-xl text-slate-400 active:text-slate-700 active:bg-slate-100 transition-all touch-target"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-fredoka font-bold text-sm md:text-lg text-slate-800 truncate flex-1 text-center mx-1">
            {doc.title}
          </h1>
          <ReadingTimer
            onComplete={(secs) => setReadingTime(secs)}
            isReading={isPlaying}
          />
          <button
            data-testid="reader-settings-btn"
            onClick={() => setShowSettings(!showSettings)}
            className={`p-2.5 rounded-xl transition-all touch-target ${showSettings ? "bg-orange-100 text-orange-600" : "text-slate-400 active:bg-slate-100"}`}
          >
            {showSettings ? <X size={20} /> : <Settings size={20} />}
          </button>
        </div>
      </div>

      {/* Settings drawer */}
      {showSettings && (
        <div className="bg-white border-b-2 border-orange-100 px-4 py-4 page-enter">
          <div className="max-w-4xl mx-auto space-y-4">
            {/* Font size */}
            <div className="flex items-center justify-between">
              <span className="font-nunito font-bold text-sm text-slate-500">Taille du texte</span>
              <div className="flex items-center gap-1">
                {fontLabels.map((label, idx) => (
                  <button
                    key={idx}
                    data-testid={`font-size-${label}`}
                    onClick={() => setFontSize(idx)}
                    className={`w-10 h-10 rounded-xl font-fredoka font-bold text-sm transition-all touch-target ${
                      fontSize === idx ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-500 active:bg-slate-200"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Profile */}
            <div>
              <span className="font-nunito font-bold text-sm text-slate-500 block mb-2">Profil</span>
              <div className="flex flex-wrap gap-2">
                {profiles.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`reader-profile-${p.id}`}
                    onClick={() => setSelectedProfileId(p.id)}
                    className={`px-3 py-2 rounded-xl font-nunito font-bold text-xs transition-all touch-target ${
                      selectedProfileId === p.id
                        ? "bg-orange-500 text-white"
                        : "bg-slate-100 text-slate-600 active:bg-slate-200"
                    }`}
                  >
                    {p.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Guided mode */}
            <div className="flex items-center justify-between">
              <div>
                <span className="font-nunito font-bold text-sm text-slate-500">Mode guide</span>
                <p className="font-nunito text-xs text-slate-400">Navi suit les mots avec toi</p>
              </div>
              <button
                data-testid="guided-mode-toggle"
                onClick={() => setGuidedMode(!guidedMode)}
                className={`w-12 h-7 rounded-full transition-all flex items-center touch-target ${
                  guidedMode ? "bg-orange-500 justify-end" : "bg-slate-300 justify-start"
                }`}
              >
                <div className="w-5 h-5 bg-white rounded-full mx-1 shadow-sm" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reading area */}
      <div className="flex-1 overflow-y-auto custom-scroll px-4 md:px-8 py-6 md:py-10">
        <div className="max-w-3xl mx-auto">
          <div className={`font-lexend ${fontSizes[fontSize]} text-slate-800`}>
            {analysis.sentences.map((sentence, sentIdx) => {
              const isActive = currentSentenceIdx === sentIdx;
              const bgColor = lineAlt ? lineAlt[sentIdx % lineAlt.length] : "transparent";

              return (
                <div
                  key={sentence.id}
                  ref={(el) => { sentenceRefs.current[sentIdx] = el; }}
                  data-testid={`reader-sentence-${sentIdx}`}
                  className={`rounded-xl px-3 py-2 -mx-3 mb-1 transition-all cursor-pointer active:opacity-70 ${
                    isActive ? "ring-3 ring-orange-300 bg-orange-50" : ""
                  }`}
                  style={{ backgroundColor: isActive ? undefined : bgColor }}
                  onClick={() => ttsAvailable && playSentence(sentIdx)}
                >
                  {sentence.words.map((word, wordIdx) => {
                    const nextWord = sentence.words[wordIdx + 1];
                    const nextIsPunct = nextWord?.type === "punct";
                    const isCurrentWord = guidedMode && isActive && wordIdx === currentWordIdx;

                    if (word.type === "space") return null;
                    if (word.type === "punct") return <span key={word.id} className="text-slate-600">{word.text}{" "}</span>;

                    const trailing = nextIsPunct ? "" : " ";
                    const wordHighlight = isCurrentWord
                      ? "bg-yellow-200 rounded-lg px-0.5 -mx-0.5 transition-colors duration-200"
                      : "transition-colors duration-200";

                    if (syllableColors && word.syllables?.length > 0) {
                      const silentSet = new Set(showSilent ? (word.silent_letters || []) : []);
                      const offset = word.syllable_offset || 0;
                      return (
                        <span key={word.id} className={`inline ${wordHighlight}`}>
                          {word.syllables.map((syl, sylIdx) => {
                            const color = syllableColors[(offset + sylIdx) % syllableColors.length];
                            return (
                              <span key={sylIdx} className="syllable-arc">
                                {[...syl.text].map((char, ci) => {
                                  const absIdx = syl.start + ci;
                                  const isSilent = silentSet.has(absIdx);
                                  let isHL = false;
                                  if (highlightGraphemes && word.graphemes) {
                                    word.graphemes.forEach((g) => {
                                      if (highlightGraphemes.has(g.label) && absIdx >= g.start && absIdx < g.end) isHL = true;
                                    });
                                  }
                                  return (
                                    <span
                                      key={ci}
                                      className={`${isSilent ? "silent-letter" : ""} ${isHL ? "grapheme-highlight" : ""}`}
                                      style={isSilent ? {} : { color }}
                                    >
                                      {char}
                                    </span>
                                  );
                                })}
                              </span>
                            );
                          })}
                          {trailing}
                        </span>
                      );
                    }

                    return <span key={word.id} className={`text-slate-800 ${wordHighlight}`}>{word.text}{trailing}</span>;
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Audio controls - bottom bar */}
      <div className="sticky bottom-0 bg-white/95 backdrop-blur-xl border-t-2 border-orange-100 shadow-[0_-4px_12px_rgba(0,0,0,0.05)]"
           style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}>
        <div className="flex items-center justify-center gap-4 h-16 max-w-md mx-auto px-4">
          {guidedMode && isPlaying && (
            <img src="/LEcoleDeNavi4.png" alt="Navi" className="h-10 w-auto mascot-float absolute left-4" />
          )}
          {ttsAvailable ? (
            <>
              <button
                data-testid="reader-prev"
                onClick={handlePrev}
                className="p-3 rounded-full text-slate-400 active:text-slate-700 active:bg-slate-100 transition-all touch-target"
              >
                <SkipBack size={22} />
              </button>

              <button
                data-testid="reader-play-pause"
                onClick={handlePlayPause}
                className="w-14 h-14 rounded-full bg-orange-500 active:bg-orange-600 text-white flex items-center justify-center shadow-[0_4px_0_rgb(194,65,12)] btn-bouncy"
              >
                {isPlaying ? <Pause size={24} /> : <Play size={24} className="ml-0.5" />}
              </button>

              <button
                data-testid="reader-next"
                onClick={handleNext}
                className="p-3 rounded-full text-slate-400 active:text-slate-700 active:bg-slate-100 transition-all touch-target"
              >
                <SkipForward size={22} />
              </button>

              {isPlaying && (
                <button
                  data-testid="reader-stop"
                  onClick={handleStop}
                  className="p-3 rounded-full text-red-400 active:text-red-600 active:bg-red-50 transition-all touch-target"
                >
                  <VolumeX size={20} />
                </button>
              )}
            </>
          ) : (
            <div className="flex items-center gap-2 text-slate-400">
              <VolumeX size={18} />
              <span className="font-nunito text-sm">Audio non disponible</span>
            </div>
          )}
        </div>
      </div>

      {/* Celebration overlay */}
      <CelebrationOverlay
        show={showCelebration}
        onClose={async () => {
          setShowCelebration(false);
          // Save reading session with timer data
          if (doc && readingTime > 0) {
            const wordsCount = analysis?.sentences?.reduce(
              (sum, s) => sum + s.words.filter((w) => w.type === "word").length, 0
            ) || 0;
            try {
              await createReadingSession({
                text_id: textId,
                text_title: doc.title,
                duration_seconds: readingTime,
                words_count: wordsCount,
              });
            } catch (err) { console.error("Failed to save session:", err); }
          }
        }}
        studentName={null}
        readingTime={readingTime}
      />
    </div>
  );
}
